<template>
  <div class="acceleration-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$a = Δv / Δt$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="180" viewBox="0 0 400 180">
          <!-- 轨道 -->
          <line x1="0" y1="140" x2="400" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="2" />
          
          <!-- 物体 -->
          <g :transform="`translate(${pos.x}, 140)`">
             <rect x="-24" y="-16" width="48" height="16" fill="#1e293b" rx="2" stroke="#334155" />
             <rect x="-18" y="-4" width="8" height="8" rx="4" fill="#64748b" />
             <rect x="10" y="-4" width="8" height="8" rx="4" fill="#64748b" />
             
             <!-- 速度矢量 v -->
             <g v-if="currentV != 0">
                <line x1="0" y1="-24" :x2="currentV * 12" y2="-24" stroke="#10b981" stroke-width="3" marker-end="url(#arr-v)" />
                <text :x="currentV * 12 + 5" y="-21" fill="#10b981" font-size="11" font-weight="700">v (速度)</text>
             </g>
             
             <!-- 加速度矢量 a -->
             <g v-if="accel != 0">
                <line x1="0" y1="-44" :x2="accel * 40" y2="-44" stroke="#ef4444" stroke-width="3" marker-end="url(#arr-a)" />
                <text :x="accel * 40 + 5" y="-41" fill="#ef4444" font-size="11" font-weight="700">a (加速度)</text>
             </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>实时速度 v</label>
              <span class="val">{{ currentV.toFixed(2) }} <small>m/s</small></span>
           </div>
           <div class="hud-item orange">
              <label>设定加速度 a</label>
              <span class="val">{{ accel.toFixed(1) }} <small>m/s²</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row">
                 <div class="s-item flex-1">
                    <label>调节加速度大小与方向 (a)</label>
                    <el-slider v-model="accel" :min="-2" :max="2" :step="0.1" size="small"></el-slider>
                 </div>
              </div>
              <div class="action-row">
                 <el-button type="primary" size="small" plain @click="reset" icon="el-icon-refresh-right">重置位置并重启</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p v-if="accel * currentV > 0"><strong>加速运动：</strong>加速度与速度方向<strong>相同</strong>时，速度不断增加。</p>
           <p v-else-if="accel * currentV < 0"><strong>减速运动：</strong>加速度与速度方向<strong>相反</strong>时，速度逐渐减小。</p>
           <p v-else><strong>匀速运动：</strong>当加速度为 <strong>0</strong> 时，物体保持原有运动状态不变。</p>
        </div>
      </div>
    </div>

    <defs>
      <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="#10b981" /> </marker>
      <marker id="arr-a" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="#ef4444" /> </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'AccelerationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      accel: 0.5,
      currentV: 2,
      pos: { x: 50, y: 0 },
      running: true
    }
  },
  mounted() {
    this.animate();
  },
  methods: {
    animate() {
      if (!this.running) return;
      if (this.frameId) cancelAnimationFrame(this.frameId);
      
      this.currentV += this.accel * 0.025; // Slightly faster for visibility
      if (this.currentV < 0) this.currentV = 0;
      this.pos.x += this.currentV;

      if (this.pos.x > 385 || this.pos.x < 15) {
        this.reset();
      }
      this.frameId = requestAnimationFrame(this.animate);
    },
    reset() {
      this.pos.x = 40;
      this.currentV = 1.5;
    }
  },
  beforeDestroy() {
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId);
  }
}
</script>

<style scoped lang="scss">
.acceleration-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 180px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f97316; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } } } .action-row { border-top: 1px solid #f1f5f9; padding-top: 8px; .el-button { width: 100%; font-weight: 700; } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #2563eb; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
